import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import { useAuth } from "../services/useAuth";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";
const STORAGE_KEY = "displayCurrency";

const ExchangeRateContext = createContext(null);

function readStoredCurrency() {
  try {
    return localStorage.getItem(STORAGE_KEY) === "TRY" ? "TRY" : "USD";
  } catch {
    return "USD";
  }
}

export function readStoredDisplayCurrency() {
  return readStoredCurrency();
}

/**
 * App-wide TCMB USD rate and display currency.
 * Amounts are always stored and entered in USD; TRY is display-only.
 */
export function ExchangeRateProvider({ children }) {
  const { getToken } = useAuth();
  const [rate, setRate] = useState(null);
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [displayCurrency, setDisplayCurrency] = useState(readStoredCurrency);

  const fetchRate = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getToken?.();
      const { data } = await axios.get(`${API_URL}/api/exchange-rate`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (data?.rate) {
        setRate(data.rate);
        setDate(data.date || null);
        return data.rate;
      }
      throw new Error(data?.message || "Kur alınamadı");
    } catch (err) {
      const message =
        err?.response?.data?.message || err?.message || "Kur alınamadı";
      setError(message);
      console.error("Failed to fetch USD rate:", err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, displayCurrency);
    } catch {
      // storage unavailable, preference stays session-only
    }
  }, [displayCurrency]);

  const toggleCurrency = useCallback(async () => {
    if (displayCurrency === "TRY") {
      setDisplayCurrency("USD");
      return;
    }
    if (!rate && !(await fetchRate())) return;
    setDisplayCurrency("TRY");
  }, [displayCurrency, rate, fetchRate]);

  const usdToTry = useCallback(
    (usdAmount) => {
      if (!rate) return null;
      return (Number(usdAmount) || 0) * rate;
    },
    [rate],
  );

  const value = useMemo(
    () => ({
      rate,
      date,
      loading,
      error,
      displayCurrency,
      fetchRate,
      toggleCurrency,
      usdToTry,
    }),
    [
      rate,
      date,
      loading,
      error,
      displayCurrency,
      fetchRate,
      toggleCurrency,
      usdToTry,
    ],
  );

  return (
    <ExchangeRateContext.Provider value={value}>
      {children}
    </ExchangeRateContext.Provider>
  );
}

export function useExchangeRate() {
  const ctx = useContext(ExchangeRateContext);
  if (!ctx) {
    throw new Error("useExchangeRate must be used within ExchangeRateProvider");
  }
  return ctx;
}

export function useExchangeRateOptional() {
  return useContext(ExchangeRateContext);
}
